import {
  I2cRadioButton,
  I2cCheckbox,
  I2cRadio,
  I2cCheckboxGroup,
  I2cRadioGroup,
  I2cListItem,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { agentWebsiteLang, webRequire, expectedOperationHours, backUporOverflowOptions } from "./stringUtils";
import { handleCheckboxArrayToggle, handleInputChange, handleRadioChange } from "./formUtils";
import { validationSchemaContactCenterServices } from "./ValidationSchema";

const ContactCenterServices = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaContactCenterServices),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      contact_center_service_would_you_like_contact_center_service: "no",
      contact_center_service_toll_free_number: "",
      contact_center_service_support_language: [],
      contact_center_service_support_language_other: "",
      contact_center_service_expected_hours_of_operation_for_customer_service: "",
      contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs: "",
      contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow: "no",
      contact_center_service_support_hours_required: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "contact_center_service_would_you_like_contact_center_service",
    "contact_center_service_toll_free_number",
    "contact_center_service_support_language",
    "contact_center_service_support_language_other",
    "contact_center_service_expected_hours_of_operation_for_customer_service",
    "contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs",
    "contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow",
    "contact_center_service_support_hours_required",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_6": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_6"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_6", true);
    } else {
      setFormValidStates("3_6", false);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isWebRequired = methods.watch("contact_center_service_would_you_like_contact_center_service") === "yes";

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="sub-section">
              <div className="sub-heading mb-16">
                Would you like Contact Center Services Live Operator?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch("contact_center_service_would_you_like_contact_center_service")}
                name="contact_center_service_would_you_like_contact_center_service"
                onI2cChange={(e) => {
                  const value = String(e.target.value);
                  
                  handleRadioChange(
                    methods,
                    "contact_center_service_would_you_like_contact_center_service",
                    value,
                    value !== "yes" ? {
                      contact_center_service_toll_free_number: "",
                      contact_center_service_support_language: [],
                      contact_center_service_support_language_other: "",
                      contact_center_service_expected_hours_of_operation_for_customer_service: "",
                      contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs: "",
                      contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow: "no",
                      contact_center_service_support_hours_required: "",
                    } : {},
                    handleValueChanges
                  );
                }}
              >
                {webRequire.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.contact_center_service_would_you_like_contact_center_service && (
                <ErrorMessage message={errors.contact_center_service_would_you_like_contact_center_service.message} />
              )}
            </div>
            {isWebRequired && (
              <>
                <div className="section-space">
                  <div i2c-grid="grid gap-sm" className="mt-16">
                    <div i2c-grid="4@md">
                      <div className="single-field-wrapper">
                        <I2cInput
                          label="Toll-free number"
                          className="single-input-field"
                          onI2cInput={(e) =>
                            handleInputChange(
                              methods,
                              "contact_center_service_toll_free_number",
                              e.target.value,
                              handleValueChanges,
                            )
                          }
                          value={methods.watch(
                            "contact_center_service_toll_free_number",
                          )}
                          placeholder="1-800-123-4567"
                          name="contact_center_service_toll_free_number"
                          size="large"
                          invalid={
                            errors.contact_center_service_toll_free_number
                              ? true
                              : false
                          }
                        />
                        {errors.contact_center_service_toll_free_number && (
                          <ErrorMessage
                            show={true}
                            message={
                              errors.contact_center_service_toll_free_number
                                ?.message || "Please enter a valid phone number"
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="section-space">
                  <div className="sub-heading mb-16">CSR support Language</div>
                  <I2cCheckboxGroup
                    key={`contact-center-language-${methods.watch("contact_center_service_would_you_like_contact_center_service")}`}
                  >
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("contact_center_service_support_language").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "contact_center_service_support_language",
                              web.value,
                              { dependentField: "contact_center_service_support_language_other" },
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="ic_cAgentLang"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("contact_center_service_support_language").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.contact_center_service_support_language && (
                    <ErrorMessage message={errors.contact_center_service_support_language.message} />
                  )}
                  {methods.watch("contact_center_service_support_language").includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="contact_center_service_support_language_other"
                            value={methods.watch("contact_center_service_support_language_other")}
                            onI2cInput={(e) => 
                              handleInputChange(methods, "contact_center_service_support_language_other", e.target.value, handleValueChanges)
                            }
                            invalid={errors.contact_center_service_support_language_other ? true : false}
                          />
                          {errors.contact_center_service_support_language_other && (
                            <ErrorMessage message={errors.contact_center_service_support_language_other.message} />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="section-space">
                  <div className="sub-heading mb-16">
                    What are the expected hours of operation for customer
                    service?
                  </div>
                  <I2cRadioGroup
                    value={methods.watch("contact_center_service_expected_hours_of_operation_for_customer_service")}
                    onI2cChange={(e) => {
                      const value = e.target?.value || e;
                      
                      handleRadioChange(
                        methods,
                        "contact_center_service_expected_hours_of_operation_for_customer_service",
                        value,
                        value !== "other" ? {
                          contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs: ""
                        } : {},
                        handleValueChanges
                      );
                    }}
                  >
                    <div i2c-grid="grid gap-sm">
                      {expectedOperationHours.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          type="checkbox"
                        >
                          <I2cRadio slot="prefix" filled value={web.value} />
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cRadioGroup>
                  {errors.contact_center_service_expected_hours_of_operation_for_customer_service && (
                    <ErrorMessage message={errors.contact_center_service_expected_hours_of_operation_for_customer_service.message} />
                  )}
                  {methods.watch("contact_center_service_expected_hours_of_operation_for_customer_service") === "other" && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs"
                            value={methods.watch("contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs")}
                            onI2cInput={(e) =>
                              handleInputChange(methods, "contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs", e.target.value, handleValueChanges)
                            }
                            invalid={errors.contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs ? true : false}
                          />
                          {errors.contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs && (
                            <ErrorMessage message={errors.contact_center_service_expected_hours_of_operation_for_customer_service_custom_hrs.message} />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="section-space">
                  <div className="sub-heading mb-16">
                    Do you want i2c to provide back-up or overflow customer
                    service?
                  </div>
                  <I2cRadioGroup
                    value={methods.watch("contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow")}
                    onI2cChange={(e) => {
                      const value = e.target?.value || e;
                      
                      handleRadioChange(
                        methods,
                        "contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow",
                        value,
                        value !== "yes" ? {
                          contact_center_service_support_hours_required: ""
                        } : {},
                        handleValueChanges
                      );
                    }}
                  >
                    <div i2c-grid="grid gap-sm">
                      {backUporOverflowOptions.map((option, index) => (
                        <I2cListItem
                          key={option.value}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={option.label}
                          type="checkbox"
                        >
                          <I2cRadio slot="prefix" filled value={option.value} />
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cRadioGroup>
                  {errors.contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow && (
                    <ErrorMessage message={errors.contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow.message} />
                  )}
                  {methods.watch("contact_center_service_do_you_want_i2c_to_provide_backup_or_overflow") === "yes" && (
                    <div className="section-space">
                      <div i2c-grid="grid gap-sm" className="mt-16">
                        <div i2c-grid="4@md">
                          <div className="single-field-wrapper">
                            <I2cInput
                              label="Specify the support hours required"
                              className="single-input-field"
                              type="number"
                              onI2cInput={(e) => {
                                const value = e.target.value === '' ? '' : Number(e.target.value);
                                handleInputChange(methods, "contact_center_service_support_hours_required", value, handleValueChanges);
                              }}
                              value={methods.watch("contact_center_service_support_hours_required")}
                              placeholder="Specify the support hours required"
                              name="contact_center_service_support_hours_required"
                              size="large"
                              invalid={errors.contact_center_service_support_hours_required ? true : false}
                            />
                            {errors.contact_center_service_support_hours_required && (
                              <ErrorMessage  message={errors.contact_center_service_support_hours_required.message} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ContactCenterServices;
